import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
  Container,
} from '@mui/material';
import { CaptivePortalTemplateProps } from '../types';
import { CaptivePortalContainer, isCaptivePortalFormDataValid } from './container';
import chroma from 'chroma-js';

export const SplitTemplateDataKeys = [
  'logoUrl',
  'header',
  'content',
  'buttonText',
  'showName',
  'requireName',
  'showEmail',
  'requireEmail',
  'requireAcceptMarketing',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'primaryColor',
  'cardBackgroundColor',
  'cardHeaderColor',
  'cardTextColor',
  'cardFieldBorderColor',
  'cardFieldTextColor',
  'buttonColor',
  'buttonTextColor',
  'backgroundColor',
] as const;

export const SplitTemplate: React.FC<
  CaptivePortalTemplateProps<(typeof SplitTemplateDataKeys)[number]>
> = (props) => {
  const { data, formData, onFormChange, onSubmit, isSubmitting = false } = props;
  const { t } = useTranslation();

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormChange({
        ...formData,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaptivePortalFormDataValid(data, formData)) {
      onSubmit();
    }
  };

  return (
    <CaptivePortalContainer {...props}>
      <Grid container sx={{ minHeight: '100vh', minWidth: '100vw' }}>
        {/* Left side - Branding/Image */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            background: buildLinearGradientFromHex(data.buttonColor || '#3b82f6'),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.3,
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
            {data.logoUrl && (
              <Box
                component="img"
                src={data.logoUrl}
                alt="Logo"
                sx={{
                  maxWidth: 200,
                  maxHeight: 120,
                  mb: 4,
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            )}
            <Typography
              variant="h3"
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 3, color: data.buttonTextColor || 'white' }}
            >
              {data.header}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, color: data.buttonTextColor || 'white' }}>
              {data.content}
            </Typography>
          </Box>
        </Grid>

        {/* Right side - Form */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            backgroundColor: data.backgroundColor || '#f8fafc',
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                backgroundColor: data.cardBackgroundColor || 'white',
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  color: data.cardHeaderColor || 'primary.main',
                }}
              >
                {t('captivePortal.getStarted', 'Get Started')}
              </Typography>

              <Box component="form" onSubmit={handleFormSubmit} noValidate>
                {data.showName && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6 }}>
                      <StyledTextbox
                        data={data}
                        fullWidth
                        label={t('captivePortal.firstName', 'First Name')}
                        value={formData.firstName}
                        onChange={handleInputChange('firstName')}
                        required={data.requireName}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <StyledTextbox
                        data={data}
                        fullWidth
                        label={t('captivePortal.lastName', 'Last Name')}
                        value={formData.lastName}
                        onChange={handleInputChange('lastName')}
                        required={data.requireName}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {data.showEmail && (
                  <StyledTextbox
                    data={data}
                    fullWidth
                    label={t('captivePortal.email', 'Email Address')}
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    required={data.requireEmail}
                    variant="outlined"
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}

                {data.termsAndConditionsUrl && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.acceptedTerms || false}
                        onChange={handleInputChange('acceptedTerms')}
                        sx={{
                          color: 'rgba(0, 0, 0, 0.23)',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        {t('acceptTerms', 'I accept the')}{' '}
                        <Link
                          href={data.termsAndConditionsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: data.primaryColor || 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {t('termsConditions', 'Terms & Conditions')}
                        </Link>
                        {data.privacyPolicyUrl && (
                          <>
                            {' '}
                            {t('and', 'and')}{' '}
                            <Link
                              href={data.privacyPolicyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: data.primaryColor || 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {t('privacyPolicy', 'Privacy Policy')}
                            </Link>
                          </>
                        )}
                        .
                      </Typography>
                    }
                    sx={{ mb: 0.5, alignItems: 'center' }}
                  />
                )}

                {data.requireAcceptMarketing && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.acceptedMarketing || false}
                        onChange={handleInputChange('acceptedMarketing')}
                        sx={{
                          color: 'rgba(0, 0, 0, 0.23)',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'acceptMarketing',
                          'I would like to receive marketing communications and promotional offers',
                        )}
                      </Typography>
                    }
                    sx={{ mb: 3, alignItems: 'center' }}
                  />
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || !isCaptivePortalFormDataValid(data, formData)}
                  sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: buildLinearGradientFromHex('#3b82f6', data.buttonColor),
                    color: data.buttonTextColor || '#ffffff',
                    '&:hover': {
                      background: buildDarkenedLinearGradientFromHex('#3b82f6', data.buttonColor),
                    },
                    '&:disabled': {
                      background: buildLinearGradientFromHex('#3b82f6', data.buttonColor),
                      color: data.buttonTextColor || '#ffffff',
                      transform: 'none',
                      opacity: 0.7,
                    },
                  }}
                >
                  {isSubmitting
                    ? t('captivePortal.connecting', 'Connecting...')
                    : data.buttonText || t('captivePortal.connect', 'Connect to Internet')}
                </Button>
              </Box>
            </Paper>
          </Container>
        </Grid>
      </Grid>
    </CaptivePortalContainer>
  );
};

function StyledTextbox(
  props: ComponentProps<typeof TextField> & {
    data: CaptivePortalTemplateProps<(typeof SplitTemplateDataKeys)[number]>['data'];
  },
) {
  const { data, ...rest } = props;
  return (
    <TextField
      {...props}
      variant="outlined"
      fullWidth
      sx={{
        ...rest.sx,
        '& .MuiInputLabel-root': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiInputBase-input': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused fieldset': {
            borderColor: data.buttonColor || data.primaryColor || 'primary.main',
          },
        },
      }}
    />
  );
}

function buildLinearGradientFromHex(defaultColor: string, color?: string): string {
  color = color || defaultColor;
  const color1 = chroma(color).darken(1.5).hex();
  const color2 = chroma(color).brighten(1.5).hex();
  return `linear-gradient(45deg, ${color1} 0%, ${color} 50%, ${color2} 100%)`;
}

function buildDarkenedLinearGradientFromHex(defaultColor: string, color?: string): string {
  color = color || defaultColor;
  const color1 = chroma(color).darken(1.5).hex();
  const color2 = chroma(color).darken(0.5).hex();
  return `linear-gradient(45deg, ${color1} 0%, ${color2} 100%)`;
}
